import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import type * as monaco from "monaco-editor";
import { default as MonacoEditorComponent } from "@monaco-editor/react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import {
  ZoomIn,
  ZoomOut,
  RotateCw,
  WrapText,
  Copy,
  Download,
  AlertCircle,
} from "lucide-react";
import { LoadingSpinner } from "../../../components/ui/loadingSpinner/loadingSpinner";
import { useTheme } from "../../../contexts/themeContext";

interface MonacoTextViewerProps {
  signedUrl: string;
  fileName?: string;
  elementId?: string;
}

const MonacoTextViewer: React.FC<MonacoTextViewerProps> = ({
  signedUrl,
  fileName,
  elementId,
}) => {
  const { resolvedTheme } = useTheme();
  const [content, setContent] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fontSize, setFontSize] = useState(14);
  const [wordWrap, setWordWrap] = useState<"on" | "off">("off");
  const [citationLineNumber, setCitationLineNumber] = useState<number | null>(
    null,
  );
  const [citationSnippet, setCitationSnippet] = useState<string | null>(null);
  const [lineCount, setLineCount] = useState(0);
  const [isMonacoLoaded, setIsMonacoLoaded] = useState(false);
  const [MonacoEditor, setMonacoEditor] = useState<
    typeof MonacoEditorComponent | null
  >(null);

  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<typeof monaco | null>(null);
  const citationDecorationsRef = useRef<string[]>([]);
  const [isEditorReady, setIsEditorReady] = useState(false);

  // Load Monaco Editor dynamically
  useEffect(() => {
    const loadMonaco = async () => {
      try {
        const monacoModule = await import("@monaco-editor/react");
        setMonacoEditor(() => monacoModule.default);
        setIsMonacoLoaded(true);
      } catch (err) {
        console.error("Failed to load Monaco editor:", err);
        setError("Failed to load Monaco editor");
      }
    };

    loadMonaco();
  }, []);

  // Detect language from file extension
  const language = useMemo(() => {
    if (!fileName) return "plaintext";
    const ext = fileName.split(".").pop()?.toLowerCase();
    const languageMap: Record<string, string> = {
      js: "javascript",
      jsx: "javascript",
      ts: "typescript",
      tsx: "typescript",
      json: "json",
      html: "html",
      css: "css",
      scss: "scss",
      less: "less",
      xml: "xml",
      md: "markdown",
      py: "python",
      java: "java",
      c: "c",
      cpp: "cpp",
      cs: "csharp",
      go: "go",
      rs: "rust",
      php: "php",
      rb: "ruby",
      sql: "sql",
      sh: "shell",
      yaml: "yaml",
      yml: "yaml",
      log: "plaintext",
      txt: "plaintext",
    };
    return languageMap[ext || ""] || "plaintext";
  }, [fileName]);

  // Parse elementId for citation navigation
  const parseElementId = useCallback((elementId: string) => {
    if (!elementId) return null;

    // Format: line_{line_number}#{text_snippet}
    const match = elementId.match(/^line_(\d+)#(.+)$/);
    if (!match) return null;

    const lineNumber = parseInt(match[1], 10);
    const textSnippet = match[2];

    return { lineNumber, textSnippet };
  }, []);

  // Load file
  useEffect(() => {
    const loadFile = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(signedUrl);
        if (!response.ok) {
          throw new Error(`Failed to load file: ${response.statusText}`);
        }

        const text = await response.text();
        setContent(text);
        setLineCount(text.split("\n").length);
        setIsLoading(false);
      } catch (err) {
        console.error("Error loading text file:", err);
        setError(err instanceof Error ? err.message : "Failed to load file");
        setIsLoading(false);
      }
    };

    loadFile();
  }, [signedUrl]);

  // Handle citation navigation when elementId changes
  useEffect(() => {
    if (elementId && isEditorReady && editorRef.current) {
      const parsed = parseElementId(elementId);
      if (parsed) {
        const { lineNumber, textSnippet } = parsed;
        setCitationLineNumber(lineNumber);
        setCitationSnippet(textSnippet);

        // Navigate to the line
        setTimeout(() => {
          if (editorRef.current) {
            editorRef.current.revealLineInCenter(lineNumber);
            editorRef.current.setPosition({ lineNumber, column: 1 });
          }
        }, 100);
      }
    } else if (!elementId) {
      setCitationLineNumber(null);
      setCitationSnippet(null);
    }
  }, [elementId, isEditorReady, parseElementId]);

  // Update citation highlight
  useEffect(() => {
    if (!isEditorReady || !editorRef.current || !monacoRef.current) return;

    // Clear previous citation decorations
    citationDecorationsRef.current = editorRef.current.deltaDecorations(
      citationDecorationsRef.current,
      [],
    );

    if (citationLineNumber && citationSnippet) {
      const model = editorRef.current.getModel();
      if (!model) return;

      const lineContent = model.getLineContent(citationLineNumber);
      const snippetIndex = lineContent.indexOf(citationSnippet);

      if (snippetIndex !== -1) {
        const decorations: monaco.editor.IModelDeltaDecoration[] = [
          {
            range: new monacoRef.current.Range(
              citationLineNumber,
              1,
              citationLineNumber,
              lineContent.length + 1,
            ),
            options: {
              isWholeLine: true,
              className: "citation-line-highlight",
              glyphMarginClassName: "citation-glyph-margin",
            },
          },
          {
            range: new monacoRef.current.Range(
              citationLineNumber,
              snippetIndex + 1,
              citationLineNumber,
              snippetIndex + citationSnippet.length + 1,
            ),
            options: {
              inlineClassName: "citation-snippet-highlight",
            },
          },
        ];

        citationDecorationsRef.current = editorRef.current.deltaDecorations(
          [],
          decorations,
        );
      }
    }
  }, [citationLineNumber, citationSnippet, isEditorReady]);

  // Zoom controls
  const handleZoomIn = () => setFontSize((prev) => Math.min(prev + 2, 32));
  const handleZoomOut = () => setFontSize((prev) => Math.max(prev - 2, 8));
  const handleResetZoom = () => setFontSize(14);

  // Toggle word wrap
  const toggleWordWrap = () => {
    setWordWrap((prev) => (prev === "on" ? "off" : "on"));
  };

  // Copy to clipboard
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(content);
  }, [content]);

  // Download file
  const handleDownload = useCallback(() => {
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "document.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [content, fileName]);

  // Handle editor mount
  const handleEditorDidMount = (
    editorInstance: monaco.editor.IStandaloneCodeEditor,
    monacoInstance: typeof monaco,
  ) => {
    editorRef.current = editorInstance;
    monacoRef.current = monacoInstance;

    // Add custom CSS for citation highlighting
    const style = document.createElement("style");
    style.innerHTML = `
      .citation-line-highlight {
        background-color: rgba(253, 224, 71, 0.2);
        border-left: 4px solid rgb(234, 179, 8);
      }
      .citation-snippet-highlight {
        background-color: rgba(253, 224, 71, 0.5);
        border-bottom: 2px solid rgb(202, 138, 4);
      }
      .citation-glyph-margin {
        background-color: rgb(234, 179, 8);
        width: 4px !important;
      }
    `;
    document.head.appendChild(style);

    // Open the find widget by default
    setTimeout(() => {
      editorInstance.trigger("MonacoTextViewer", "actions.find", null);
    }, 100);

    // Mark editor as ready
    setIsEditorReady(true);
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!isMonacoLoaded || !MonacoEditor) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-2">
          <LoadingSpinner />
          <span className="text-sm text-gray-700">Loading editor...</span>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-2">
          <LoadingSpinner />
          <span className="text-sm text-gray-700">Loading file...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col bg-white">
      {/* Citation Indicator */}
      {citationLineNumber && citationSnippet && (
        <div className="bg-yellow-50 border-b border-yellow-200 p-3">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-medium text-yellow-900">
                Citation Location
              </div>
              <div className="text-xs text-yellow-700 font-mono mt-1">
                Line {citationLineNumber}: "
                {citationSnippet.length > 100
                  ? citationSnippet.substring(0, 100) + "..."
                  : citationSnippet}
                "
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2 p-2 border-b border-stone-200 bg-stone-50">
        {/* Controls */}
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            onClick={handleZoomOut}
            title="Decrease font size"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-sm text-stone-600 w-12 text-center">
            {fontSize}px
          </span>
          <Button
            variant="outline"
            size="icon"
            onClick={handleZoomIn}
            title="Increase font size"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={handleResetZoom}
            title="Reset font size"
          >
            <RotateCw className="h-4 w-4" />
          </Button>
          <Button
            variant={wordWrap === "on" ? "default" : "outline"}
            size="icon"
            onClick={toggleWordWrap}
            title="Toggle word wrap"
          >
            <WrapText className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={handleCopy}
            title="Copy to clipboard"
          >
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={handleDownload}
            title="Download file"
          >
            <Download className="h-4 w-4" />
          </Button>
        </div>

        {/* File info */}
        <div className="text-sm text-stone-600 whitespace-nowrap">
          {lineCount.toLocaleString()} lines
        </div>
      </div>

      {/* Monaco Editor */}
      <div className="flex-1 overflow-hidden">
        <MonacoEditor
          height="100%"
          defaultLanguage={language}
          value={content}
          onMount={handleEditorDidMount}
          options={{
            readOnly: true,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            fontSize,
            wordWrap,
            automaticLayout: true,
            lineNumbers: "on",
            renderWhitespace: "selection",
            lineNumbersMinChars: 3,
            folding: true,
            lineDecorationsWidth: 10,
            contextmenu: true,
            scrollbar: {
              vertical: "visible",
              horizontal: "visible",
              useShadows: false,
              verticalScrollbarSize: 10,
              horizontalScrollbarSize: 10,
            },
            find: {
              addExtraSpaceOnTop: false,
              autoFindInSelection: "never",
              seedSearchStringFromSelection: "never",
            },
            glyphMargin: true,
          }}
          theme={resolvedTheme === "dark" ? "vs-dark" : "vs"}
        />
      </div>
    </div>
  );
};

export default MonacoTextViewer;
